import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import CheckBoxInputWithLabel from '../../../components/fields/CheckBoxInput/CheckBoxInputWithLabel';

const animationTimeout = 200;

export default function FieldList(props) {
  const {
    group, setChildren = () => {}, setOpenedFields = () => {}, openedFields = null, handleFieldChange = () => {}, searchField = '',
  } = props;
  const { children } = group;
  const [items, setItems] = useState(children);
  const [newIndex, setNewIndex] = useState(0);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [styleGhost, setStyleGhost] = useState(null);
  const [targetElementId, setTargetElementId] = useState(null);
  const [targetPos, setTargetPos] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const wrapperRef = useRef(null);
  const ghostRef = useRef(null);
  const { formatMessage } = useIntl();
  const blockHeight = 40;

  // useEffect(() => {
  //   const fields = group?.children?.filter(g => String(formatMessage({ id: g.dto.labelKey, defaultMessage: g.dto.labelKey })).toLowerCase().includes(searchField.toLowerCase())) || [];
  //   setItems(fields);
  // }, [JSON.stringify(group), searchField]);

  useEffect(() => {
    setItems(group?.children || []);
  }, [JSON.stringify(group)]);

  const calculateOrder = event => {
    const parentRect = wrapperRef.current.getBoundingClientRect();
    const relativeY = event.clientY - parentRect.top + wrapperRef.current.scrollTop;
    let hoverIndex = Math.floor(relativeY / blockHeight);
    const ost = relativeY % blockHeight;
    if (ost > blockHeight / 2) {
      hoverIndex++;
    }

    if (hoverIndex > targetIndex) {
      hoverIndex--;
    }
    hoverIndex = Math.max(0, Math.min(hoverIndex, children.length - 1));
    setNewIndex(hoverIndex);
    setVisibleIndex(hoverIndex <= targetIndex ? hoverIndex : hoverIndex + 1);
  };

  const onMouseDown = e => {
    if (searchField) return;
    const tId = e?.target?.parentElement?.parentElement?.id || '';
    const selected = items.find(i => `${group.id}-${i.dto.name}` === tId);
    if (selected && !selected.disablePosition) {
      document.body.classList.add('drag-cursor');
      const targetRect = e.target.parentElement.parentElement.getBoundingClientRect();
      setTargetPos({
        y: e.clientY - targetRect.y,
        x: e.clientX - targetRect.x,
        top: targetRect.top,
        left: targetRect.left,
        width: targetRect.width,
        height: targetRect.height,
        scrollTop: wrapperRef.current.scrollTop,
      });
      setStyleGhost({
        top: `${targetRect.top}px`,
        left: `${targetRect.left}px`,
        width: `${targetRect.width}px`,
      });
      setTargetIndex(selected.sortIndex);
      setTargetElementId(tId);
      setTargetIndex(selected.sortIndex);
      setVisibleIndex(selected.sortIndex);
    }
  };

  const onMouseMove = event => {
    if (targetElementId && ghostRef?.current) {
      const top = event.clientY - targetPos.y;
      ghostRef.current.style.top = `${top}px`;
      ghostRef.current.style.left = `${targetPos.left}px`;
      ghostRef.current.style.width = `${targetPos.width}px`;
      setStyleGhost({
        top: `${top}px`,
        left: `${targetPos.left}px`,
        width: `${targetPos.width}px`,
      });
      calculateOrder(event);
    }
  };

  const onMouseUp = e => {
    if (!targetElementId) return;
    document.body.classList.remove('drag-cursor');
    const newChildren = [...items]
      .sort((a, b) => a.sortIndex - b.sortIndex);
    const [movedItem] = newChildren.splice(targetIndex, 1);
    newChildren.splice(newIndex, 0, movedItem);
    const updatedChildren = newChildren.map((item, index) => ({
      ...item,
      sortIndex: index,
    }));

    setTargetPos(null);
    setTargetElementId(null);
    setTargetIndex(null);
    setVisibleIndex(null);
    setChildren(updatedChildren);
  };

  const renderItem = (i, key) => {
    const isEnable = i.enable;
    const isEvery = i?.options?.length ? i.options.every(c => c.enable) : isEnable;
    const isSome = i?.options?.length && i.options.some(c => c.enable) && !isEvery && isEnable;
    const label = (group?.labelPrefix || i?.labelPrefix || '') + (i?.labelKey || i?.dto?.labelKey || '');
    return (
      <div
        key={`${group.id}-${i.dto.name}`}
        className={classNames('available-field', { opened: openedFields === i.dto.name }, i?.className || '')}
        style={{
          top: i.sortIndex * blockHeight,
          ...i.style,
        }}
        data-info={i.sortIndex}
        id={`${group.id}-${i.dto.name}`}
        ref={i?.ghostRef || null}
      >
        <div className="available-group-name">
          <div className="group-check">
            <CheckBoxInputWithLabel
              type="text"
              inputClassName={classNames({ 'some-child': isSome })}
              name={`group-${key}-${i.dto.name}`}
              label={formatMessage({ id: label, defaultMessage: label })}
              withLabel
              addPlaceholder
              inputProps={{
                value: isEnable,
                onChange: e => handleFieldChange(i),
              }}
            />
          </div>
          <div
            className="group-toggle-drag"
            onMouseDown={onMouseDown}
          >
            <Icons customIcon width={24} height={24} icon="draggable" />
          </div>
          {i?.options?.length ? (
            <div
              className="group-toggle-select"
              onClick={() => setOpenedFields(openedFields !== i.dto.name ? i.dto.name : null)}
            >
              <Icons customIcon className="select-arrow" width={14} height={14} icon="arrow-down" />
            </div>
          ) : (<div className="group-toggle-select-empty" />)}
        </div>
      </div>
    );
  };

  function TargetElement() {
    if (!targetElementId || !targetPos || !styleGhost?.top) return null;
    const elem = items.find(i => `${group.id}-${i.dto.name}` === targetElementId);
    if (!elem) return null;
    const ghostProps = {
      ...elem,
      className: 'ghost',
      style: styleGhost,
      id: `ghost-${targetElementId}`,
      ghostRef,
    };
    return ReactDOM.createPortal(renderItem(ghostProps, 'ghost'), document.body);
  }

  return (
    <div className={classNames('draggable-fields-wrapper custom-scroll-theme', { 'disable-drag': searchField })}>
      <div
        className="draggable-fields"
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        ref={wrapperRef}
      >
        {targetElementId && visibleIndex >= 0 && (<div className="insert-line" style={{ top: visibleIndex * blockHeight }} />)}
        {items.map(renderItem)}
        <TargetElement />
      </div>
    </div>
  );
}
